import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { recategorizeDoc } from "../api";
import { pollJob } from "@api/jobApi.ts";
import { useProfileStore } from "@pages/ClientProfile/store/profileStore.ts";
import { FORM2ID } from "@/constants.ts";
import type { DocumentUserType } from "@pages/ClientProfile/types.ts";

interface RecategorizePayload {
  documentId: string;
  userType: DocumentUserType;
  newForm: string;
}

export const useRecategorizeDocs = (): {
  recategorize: (payload: RecategorizePayload) => Promise<boolean>;
  isRecategorizing: boolean;
  error: string | null;
} => {
  const { setOcrProcessingDocId } = useProfileStore();
  const queryClient = useQueryClient();
  const [isRecategorizing, setIsRecategorizing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recategorize = async (
    payload: RecategorizePayload
  ): Promise<boolean> => {
    setIsRecategorizing(true);
    setError(null);
    setOcrProcessingDocId(payload.documentId);
    try {
      payload.newForm =
        payload.newForm in FORM2ID ? FORM2ID[payload.newForm] : payload.newForm;
      payload.newForm = payload.newForm.toLowerCase();
      const job = await recategorizeDoc(payload);

      // Check if OCR job was created or if it was a user-type-only change
      if (job.id === "no-ocr-needed") {
        // No OCR needed, just invalidate queries to refresh the UI
        console.log("No OCR needed, user type changed only");
      } else {
        // OCR job was created, poll for completion
        await pollJob(job.id);
      }

      // ✅ Invalidate the client-profile query after success
      await queryClient.invalidateQueries({ queryKey: ["client-profile"] });

      return true;
    } catch (err) {
      console.error("Recategorization failed:", err);
      setError("Failed to recategorize document");
      return false;
    } finally {
      setIsRecategorizing(false);
      setOcrProcessingDocId(null);
    }
  };

  return {
    recategorize,
    isRecategorizing,
    error,
  };
};
